/* eslint-disable import/no-cycle */
/* eslint-disable no-param-reassign */
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

import {
  Component, Inject, Input,
  OnInit, QueryList,
  ViewChild, ViewChildren, OnChanges,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { BULB_BUSY_COVER_SERVICE } from 'src/app/angularjs.providers';
import { ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { AeroFormComponent } from 'src/aero/aero-form/aero-form.component';
import { AeroCalendarPicker } from 'src/aero/aero-calendar-picker/aero-calendar-picker.model';
import { TranslationService } from 'src/common/translation.service';
import { ResumeEducationSubSection } from './education-sub-section.model';
import { ResumeSection } from '../section/section.model';
import { ResumeComponent } from '../resume.component';
import { ResumeSectionType } from '../section/resume-section-type.enum';
import { ResumeSubSectionComponent } from '../sub-section/sub-section.component';
import { SectionDataAlertService } from '../section/section-data-alert.service';
import { UnitSimpleDTO } from '../content-selection-modal/unit-simple-dto.model';

/**
 * Angular component that will house the 'Education' resume section.
 */
@Component({
  selector: 'bulb-resume-education',
  templateUrl: './education.component.html',
  standalone: false,
})
export class ResumeEducationComponent implements OnInit, OnChanges {
  /**
   * Reference to list of forms element in the DOM.
   */
  @ViewChildren(AeroFormComponent) public aeroFormComponentList:
  QueryList<AeroFormComponent> = new QueryList<AeroFormComponent>();

  /**
   * About me section model.
   */
  @Input() educationSection: ResumeSection;

  /**
   * Whether the current user can edit the section.
   */
  @Input() canEdit: boolean;

  /**
   * Reference to the parent resume component.
   */
  @Input() resumeComponent: ResumeComponent;

  /**
   * Aero form component reference.
   */
  public aeroFormComponent: AeroFormComponent;

  /**
   *  Boolean indicating if the current accordion tab is open.
   */
  public isTabOpen: boolean = false;

  /**
   * Index of subsection for edition.
   */
  private isNewEntry: boolean;

  /**
   * Whether the 'Add Entry' button is displayed in the view mode.
   */
  public showAddEntryButton: boolean = true;

  /**
   * Whether the section is showing the edit form or not.
   */
  public editMode: boolean = false;

  /**
   * Tracks for the tab changes were made in session.
   */
  public tabChange: boolean;

  /**
   * Reference to the resume experience sub-section
   */
  @ViewChild(ResumeSubSectionComponent) subSectionComponent: ResumeSubSectionComponent;

  /**
   * Boolean indicating if the user has saved the data in this section.
   * @private
   */
  private hasSaved: boolean = false;

  /**
   * Clone of the about me sub-section data to use during edit, so any changes can be cancelled or saved as needed.
   */
  public educationSubSectionClone: Array< ResumeEducationSubSection > = new Array<ResumeEducationSubSection>();

  /**
   * Array of portfolio content that the user has removed.
   */
  public removedPortfolioContent : Array< UnitSimpleDTO > = new Array<UnitSimpleDTO>();

  /**
   * Number indicating how many units a user has selected to display in their portfolio content.
   *
   * @private
   */
  private numberOfSelectedContent: number = 0;

  /**
   * Number indicating how many units a user has removed from their portfolio content.
   */
  public numberOfRemovedContent : number = 0;

  /**
   * Date picker configuration object.
   */
  public datepickerConfig : AeroCalendarPicker = new AeroCalendarPicker();

  /**
   * Button style for edit and delete aero button
   */
  public editAndDeleteButtonStyle : ButtonStyle = ButtonStyle.TEXT_WITH_ICON;

  /**
   * Button style for add portfolio content aero button
   */
  public addPortfolioContentButtonStyle : ButtonStyle = ButtonStyle.PRIMARY_2;

  /**
   * Translated text for the end date placeholder.
   */
  public endDatePlaceholder : string = '';

  /**
   * boolean indicating whether any subsection has logo.
   */
  public isLogoUploaded: boolean = false;

  /**
   * Index of currently editing subsection
   * @private
   */
  private currentIndex: number = -1;

  /**
   * Default Constructor.
   *
   * @param bulbModalService
   * @param BulbBusyCoverService
   * @param sectionDataAlertService
   * @param $translate
   */
  constructor(
    @Inject('BulbModalService') private bulbModalService,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
    private sectionDataAlertService : SectionDataAlertService,
    private $translate: TranslationService,
  ) {}

  /**
   * A callback method that is invoked immediately after the default change detector has checked the directive's
   * data-bound properties for the first time, and before any of the view or content children have been checked.
   */
  public ngOnInit(): void {
    this.translateKeywords();
    ResumeEducationComponent.configureCalender(this.datepickerConfig);
    // If the education section has been populated, we default the accordion tab to be open.
    if (this.tabChange) {
      this.isTabOpen = JSON.parse(sessionStorage.getItem('educationTabOpen'));
    } else if (!this.educationSection.empty && !this.tabChange) {
      this.isTabOpen = true;
    } else {
      sessionStorage.educationTabOpen = false;
    }
    this.cloneSubSection();
    this.showAddEntryButton = this.canEdit;
  }

  /**
   * Response handler for edit event from section component which moves us into 'edit' mode.
   *
   * @param subSectionIndex
   */
  public beginEdit(subSectionIndex : number = -1) {
    this.showAddEntryButton = false;
    this.isTabOpen = true;
    sessionStorage.educationTabOpen = true;
    this.editMode = true;
    if (subSectionIndex < 0) {
      this.addEntry();
    } else {
      this.currentIndex = subSectionIndex;
      this.editEntryMode(subSectionIndex);
      // Update service index to manage editing current sub-section.
      setTimeout(() => {
        const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[subSectionIndex];
        this.sectionDataAlertService.enableAlert(aeroFormComponent, () => {
          this.cancelEdit(subSectionIndex);
        });
      });
    }
  }

  /**
   * Callback method when the image upload is complete
   * @param $event
   */
  onUploadComplete($event) {
    this.educationSubSectionClone[this.currentIndex].logoUrl = $event;
    const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[this.currentIndex];
    this.sectionDataAlertService.handleSaveOnContentUpdate(aeroFormComponent);
  }

  /**
   * Handle closing of the accordion on the header tab click.
   */
  handleTabClose() : void {
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.isTabOpen = false;
    sessionStorage.educationTabOpen = false;
    this.editMode = false;
  }

  /**
   * Handles open of tab
   */
  handleTabOpen() {
    this.isTabOpen = true;
    sessionStorage.educationTabOpen = true;
  }

  /**
   * Handle adding the user's potential portfolio content to about-me section.
   */
  public addPortfolioContent(index : number) : void {
    this.subSectionComponent.addPortfolioContent(this, index);
  }

  /**
   * Helper method to update the portoFolioContent object identity so that the Angular detection cycle could detect
   * the object change after the removal of any portfolio content.
   *
   * @param event
   * @param index
   */
  public onPortfolioContentRemoved(event : any, index : number) : void {
    this.subSectionComponent.removePortfolioContent(this, index);
  }

  /**
   * Method that handles removing any unsaved portfolio content from the view.
   *
   * @param index
   */
  public handleUnsavedPortfolioContent(index : number) : void {
    this.subSectionComponent.handleUnsavedPortfolioContent(this, index);
  }

  /**
   * Cancel button handler.
   */
  public cancelEdit(index) {
    if (this.educationSection.empty) {
      this.isTabOpen = false;
      sessionStorage.educationTabOpen = false;
    } else {
      this.editMode = false;
    }
    this.handleUnsavedPortfolioContent(index);
    if (this.aeroFormComponent) {
      this.aeroFormComponent.markAeroFormAsPristine();
    }
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.sectionDataAlertService.disableAlert();
    if (this.isNewEntry) {
      this.educationSubSectionClone.pop();
      this.isNewEntry = false;
    } else {
      this.cloneSubSection();
    }
    this.showAddEntryButton = true;
  }

  /**
   * Method to save the Education information.
   *
   * @param resumeEducationInfo
   * @param index
   */
  public async save(resumeEducationInfo: NgForm, index : number) {
    const selectedSubsection: any = <ResumeEducationSubSection>cloneDeep(this.educationSubSectionClone[index]);

    // Convert the start date in YYYY-MM-DD format.
    selectedSubsection.educationStartDate = resumeEducationInfo.value.educationStartDate
      ? moment(resumeEducationInfo.value.educationStartDate).format('YYYY-MM-DD') : null;

    // Convert the end date in YYYY-MM-DD format.
    selectedSubsection.educationEndDate = resumeEducationInfo.value.educationEndDate
      ? moment(resumeEducationInfo.value.educationEndDate).format('YYYY-MM-DD') : null;

    if (selectedSubsection.portfolioContent && selectedSubsection.portfolioContent.length > 0) {
      // Update the clone with the attached portfolio content
      const portfolioContentDTO : Array< any > = new Array< any >();
      selectedSubsection.portfolioContent.forEach((unit) => {
        const unitDTO : any = {
          id: unit.id,
        };
        portfolioContentDTO.push(unitDTO);
      });
      selectedSubsection.portfolioContent = portfolioContentDTO;
    }

    if (index >= 0) {
      this.educationSection.subSections[index] = selectedSubsection;
    } else {
      this.educationSection.subSections.push(selectedSubsection);
    }

    this.sortEntriesByDate();
    delete selectedSubsection.editMode;
    delete selectedSubsection.endDatePlaceholder;
    this.educationSection.empty = false;

    const savePromise = this.resumeComponent.save().then((response) => {
      this.hasSaved = true;
      this.isNewEntry = false;
      this.educationSection.subSections = response.sections
        .find((section) => (section.type === ResumeSectionType.Education)).subSections;
      resumeEducationInfo.resetForm(resumeEducationInfo.value);
      this.showAddEntryButton = true;
      this.cloneSubSection();
      this.editMode = false;
    });
    this.BulbBusyCoverService.showBusyDuring(savePromise);
    this.sectionDataAlertService.resetPortfolioContentModifiedMap();
    this.sectionDataAlertService.disableAlert();
    this.sectionDataAlertService.unregisterBeforeUnloadEvent();
    this.isLogoUploaded = this.resumeComponent.checkIsLogoUploaded(this.educationSubSectionClone);
  }

  /**
   * Called when @Input parameter for this component is updated.
   * @param changes
   */
  public ngOnChanges(): void {
    this.cloneSubSection();
  }

  /*
   * Method to clone the existing educationSubSection, or create a new one if one doesn't exist.
   * @private
   */
  private cloneSubSection() {
    if (this.educationSection.subSections.length > 0) {
      this.educationSubSectionClone = <ResumeEducationSubSection[]>cloneDeep(this.educationSection.subSections);
      this.convertDateType();
      this.isLogoUploaded = this.resumeComponent.checkIsLogoUploaded(this.educationSubSectionClone);
    }
  }

  /**
   * Convert date type.
   */
  convertDateType() {
    this.educationSubSectionClone.forEach((section) => {
      section.educationStartDate = (section.educationStartDate) ? moment(section.educationStartDate).toDate() : null;
      section.educationEndDate = (section.educationEndDate) ? moment(section.educationEndDate).toDate() : null;
    });
  }

  /**
   * Set the aero-calender config picker.
   *
   * @param datepickerConfig configuration.
   */
  private static configureCalender(datepickerConfig: AeroCalendarPicker) {
    datepickerConfig.maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 10));
    datepickerConfig.minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
    datepickerConfig.range = `${datepickerConfig.minDate.getFullYear()}:${datepickerConfig.maxDate.getFullYear()}`;
  }

  /**
   * Method to add new entry in the section.
   */
  public addEntry() {
    this.showAddEntryButton = false;
    this.isNewEntry = true;
    this.editMode = true;

    if (this.educationSection.subSections.length === 0) {
      this.educationSubSectionClone = new Array<ResumeEducationSubSection>();
    }

    if (this.aeroFormComponent) {
      this.aeroFormComponent.resetFormFields();
      this.aeroFormComponent.form.resetForm(this.aeroFormComponent.form.value);
    }

    const selectedSubsection: ResumeEducationSubSection = new ResumeEducationSubSection();
    selectedSubsection.editMode = true;
    this.educationSubSectionClone.push(selectedSubsection);
    this.setFocus(this.educationSubSectionClone.length - 1);
    setTimeout(() => {
      const sectionIndex = this.educationSubSectionClone.length - 1;
      const aeroFormComponent : AeroFormComponent = this.aeroFormComponentList.toArray()[sectionIndex];
      this.sectionDataAlertService.enableAlert(aeroFormComponent, () => { this.cancelEdit(sectionIndex); });
      this.currentIndex = sectionIndex;
    });
  }

  /**
   * Populate selected entry in the form for editing.
   * @param index - selected entry index.
   */
  private editEntryMode(index: number) {
    if (this.isNewEntry) {
      this.educationSubSectionClone.pop();
      this.isNewEntry = false;
    }

    this.educationSubSectionClone[index].editMode = true;
    if (this.educationSubSectionClone[index].current) {
      this.translateKeywords();
      this.educationSubSectionClone[index].endDatePlaceholder = this.endDatePlaceholder;
    }
    this.setFocus(index);
    this.hasSaved = false;
  }

  /**
   * Sort the entries based on dates.
   */
  private sortEntriesByDate() {
    this.educationSection.subSections
      .sort((subsection1: ResumeEducationSubSection, subsection2: ResumeEducationSubSection) => {
        const firstObjectStartDate = (subsection1.educationStartDate)
          ? moment(subsection1.educationStartDate).valueOf() : 0;
        const firstObjectEndDate = (subsection1.educationEndDate)
          ? moment(subsection1.educationEndDate).valueOf() : 0;
        const secondObjectStartDate = (subsection2.educationStartDate)
          ? moment(subsection2.educationStartDate).valueOf() : 0;
        const secondObjectEndDate = (subsection2.educationEndDate)
          ? moment(subsection2.educationEndDate).valueOf() : 0;

        if (subsection1.current) {
          if (subsection2.current) {
            return (firstObjectStartDate < secondObjectStartDate) ? 1 : -1;
          }
          return -1;
        }
        if (subsection2.current) {
          if (subsection1.current) {
            return (firstObjectStartDate < secondObjectStartDate) ? 1 : -1;
          }
          return 1;
        }

        if (((firstObjectEndDate === secondObjectEndDate) && (firstObjectStartDate !== secondObjectStartDate))) {
          if (firstObjectStartDate < secondObjectStartDate) return 1;
          if (firstObjectStartDate > secondObjectStartDate) return -1;
          return 0;
        } if (subsection1.editMode && secondObjectStartDate === 0 && secondObjectEndDate === 0) {
          return -1;
        }
        if (firstObjectEndDate < secondObjectEndDate) return 1;
        if (firstObjectEndDate > secondObjectEndDate) return -1;
        return 0;
      });
  }

  /**
   * Method to set focus.
   */
  // eslint-disable-next-line class-methods-use-this
  private setFocus(index: number) {
    setTimeout(() => {
      $(`#educationSchoolName-${index}`).trigger('focus');
    });
  }

  /**
   * Method to delete entry from subsections.
   * @param index - of the entry in the subsection.
   */
  public deleteEntry(event, index) {
    event.stopPropagation();
    this.bulbModalService.show({
      fontAwesomeCode: 'fas fa-exclamation-triangle',
      titleCode: 'resume_delete_entry_confirmation_title',
      bodyCode: 'resume_delete_entry_confirmation_message',
      okButtonCode: 'button_confirm',
      cancelButtonCode: 'button_cancel',
      windowClass: 'updated-modal-design bulb-aero-modal bulb-resume-delete-entry-modal',
    }).then(() => {
      this.educationSubSectionClone.splice(index, 1);
      this.educationSection.subSections = this.educationSubSectionClone;
      if (this.educationSection.subSections.length === 0) {
        this.educationSection.empty = true;
        this.isTabOpen = false;
        sessionStorage.educationTabOpen = false;
        this.showAddEntryButton = false;
      } else {
        this.educationSection.subSections.forEach((subsection: any) => {
          subsection.educationStartDate = (subsection.educationStartDate)
            ? moment(subsection.educationStartDate).format('YYYY-MM-DD') : null;
          subsection.educationEndDate = (subsection.educationEndDate)
            ? moment(subsection.educationEndDate).format('YYYY-MM-DD') : null;
        });
      }
      this.isLogoUploaded = this.resumeComponent.checkIsLogoUploaded(this.educationSubSectionClone);
      this.resumeComponent.save();
    });
  }

  /**
   * Reset the end date and set the placeholder.
   *
   * @param isChecked true/false.
   * @param index of subsection for editing the experience.
   */
  public resetEndDate(isChecked: boolean, index: number) {
    this.educationSubSectionClone[index].endDatePlaceholder = (isChecked) ? this.endDatePlaceholder : '';
  }

  /**
   * Translate the keywords.
   */
  private translateKeywords() {
    this.endDatePlaceholder = this.$translate.translateInstant('resume_education_end_date_placeholder');
  }

  /**
   * Method to delete logo from subsection
   * @param index
   */
  public deleteLogo(index: number) {
    this.educationSubSectionClone[index].logoUrl = null;
    this.educationSubSectionClone[index].logo = null;
    const aeroFormComponent: AeroFormComponent = this.aeroFormComponentList.toArray()[index];
    this.sectionDataAlertService.handleSaveOnContentUpdate(aeroFormComponent);
  }

  /**
   *  Drag start function.
   * @param event event
   * @param section Dragged section
   */
  dragStart(event, section) {
    this.resumeComponent.dragStart(section);
  }

  /**
   * Drop function
   * @param event event
   * @param section  On dragged section
   */
  drop(event, section) {
    this.resumeComponent.drop(section);
  }
}
